import makeAllIdsReducer from '../../reducers/makeAllIdsReducer';
import makeByIdReducer from '../../reducers/makeByIdReducer';
import makeMetaReducer from '../../reducers/makeMetaReducer';
import makeSelectedReducer from '../../reducers/makeSelectedReducer';

const DEFAULT_REDUCERS = [
  { key: `allIds`, creator: makeAllIdsReducer },
  { key: `byId`, creator: makeByIdReducer },
  { key: `meta`, creator: makeMetaReducer },
  { key: `selected`, creator: makeSelectedReducer },
];

/**
 * Creates a set of reducers to store information for a CRUD models. The
 * reducers that are created for you are:
 *
 * * [allIds](#makeAllIdsReducer)
 * * [byId](#makeByIdReducer)
 * * [meta](#makeMetaReducer)
 * * [selected](#makeSelectedReducer)
 *
 * @param {Object} options The options for the creator
 * @param {string} options.model The name of the model we want constants for
 * @param {string} options.pluralModel The plural name of the model we want
 *   constants for (defaults to `${options.model}s`)
 * @param {Array} options.exclude A list of reducers to avoid generating
 * @param {Array} options.include A list of reducers to exclusively generate
 * @return {Object} An object with all of the constants
 *
 * @public
 * @author Connor Taylor
 * @example
 * import { combineReducers } from 'redux';
 *
 * // This will generate all default reducers
 * const reducers = makeModelReducers({ model: `foo` });
 *
 * // Use the `exclude` parameter to prevent an action from being generated
 * // This set will have all default reducers except for `selected`
 * reducers = makeModelReducers({ model: `foo`, exclude: [`selected`] });
 *
 * // Use the `include` parameter when you want to only generate the specified
 * // reducers. This set will only have the `selected` reducer.
 * reducers = makeModelReducers({ model: `foo`, include: [`selected`] });
 *
 * // Use the `pluralModel` parameter when you want to use a different plural
 * // version of the model. This defaults to `${model}s`.
 * reducers = makeModelReducers({ model: `bary`, pluralModel: `baries` });
 *
 * // Once you have the set of reducers you want, be sure to combine them!
 * const rootReducer = combineReducers(reducers);
 */
const makeModelReducers = ({
  model,
  pluralModel = null,
  customConfigs = {},
  include = [],
  exclude = [],
}) => {
  const filter = (accum, { key, creator }) => {
    const notIncluded = () => include.length !== 0 && !include.includes(key);
    const excluded = () => exclude.length !== 0 && exclude.includes(key);

    return notIncluded() || excluded()
      ? accum
      : {
        ...accum,
        [key]: creator({
          model,
          pluralModel,
          customConfig: customConfigs[key] || {},
        }),
      };
  };

  return DEFAULT_REDUCERS.reduce(filter, {});
};

/** @component */
export default makeModelReducers;
